import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, FileText, Eye, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const SLOTS = [
  { type: "id", label: "Identity Card", hint: "National ID — front & back" },
  { type: "passport", label: "Passport", hint: "Photo page" },
  { type: "proof_of_funds", label: "Proof of Funds", hint: "Bank statement / attestation" },
];

export const KycDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null);
  const inputs = useRef({});

  const refresh = () =>
    api.get("/kyc/my-documents").then(({ data }) => setDocs(data)).catch(() => setDocs([]));

  useEffect(() => {
    refresh();
  }, []);

  const onUpload = async (docType, file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "webp", "pdf"].includes(ext)) {
      return toast.error("Only images (jpg, png, webp) and PDF are accepted.");
    }
    setBusy(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await api.post("/kyc/upload", fd);
      toast.success("Document uploaded securely.");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setBusy(null);
    }
  };

  const onView = async (doc) => {
    try {
      const res = await api.get(`/files/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Unable to open the document.");
    }
  };

  const onDelete = async (doc) => {
    try {
      await api.delete(`/kyc/${doc.id}`);
      toast.success("Document removed.");
      await refresh();
    } catch {
      toast.error("Could not remove the document.");
    }
  };

  const byType = (t) => docs.filter((d) => d.doc_type === t);

  return (
    <div className="mt-16" data-testid="kyc-section">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-gold" />
        <h2 className="font-serif text-white text-2xl">Verification Documents</h2>
      </div>
      <p className="font-sans text-white/45 text-sm mb-8 max-w-2xl">
        To access confidential mandates, please provide two identity documents and a proof of available funds.
        All files are stored securely and shared only with your advisor.
      </p>

      <div className="grid md:grid-cols-3 gap-6">
        {SLOTS.map((slot) => {
          const existing = byType(slot.type);
          return (
            <motion.div
              key={slot.type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`kyc-slot-${slot.type}`}
              className="glass rounded-xl p-6 flex flex-col"
            >
              <p className="font-serif text-white text-xl">{slot.label}</p>
              <p className="font-sans text-white/40 text-xs mt-1 mb-5">{slot.hint}</p>

              <div className="space-y-2 mb-5 flex-1">
                {existing.length === 0 ? (
                  <p className="font-sans text-white/30 text-xs italic">No document uploaded yet.</p>
                ) : (
                  existing.map((d) => (
                    <div key={d.id} data-testid="kyc-doc-item" className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                      <FileText className="w-4 h-4 text-gold shrink-0" />
                      <span className="font-sans text-white/70 text-xs truncate flex-1">{d.original_filename}</span>
                      <button onClick={() => onView(d)} data-testid="kyc-view" className="text-white/50 hover:text-gold transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => onDelete(d)} data-testid="kyc-delete" className="text-white/50 hover:text-red-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <input
                ref={(el) => (inputs.current[slot.type] = el)}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                data-testid={`kyc-input-${slot.type}`}
                onChange={(e) => onUpload(slot.type, e.target.files?.[0])}
              />
              <button
                onClick={() => inputs.current[slot.type]?.click()}
                disabled={busy === slot.type}
                data-testid={`kyc-upload-${slot.type}`}
                className="flex items-center justify-center gap-2 border border-gold/40 text-gold py-3 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold hover:text-navy transition-all duration-400 disabled:opacity-60"
              >
                <Upload className="w-3.5 h-3.5" />
                {busy === slot.type ? "Uploading…" : existing.length ? "Replace / Add" : "Upload"}
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
